import React, { useEffect, useState } from 'react';
import { FileText, Download, MapPin, Building2, Briefcase, Loader2 } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IntelligenceSnapshot = () => {
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedState, setSelectedState] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/intelligence/filters`).then(setFilterOptions).catch(() => {});
  }, []);

  const loadPreview = async () => {
    if (!selectedState && !selectedIndustry) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedState) params.set('state', selectedState);
      if (selectedIndustry) params.set('industry', selectedIndustry);
      const data = await fetchWithAuth(`${API_URL}/api/intelligence/snapshot?${params}`);
      setPreview(data);
    } catch { setPreview(null); }
    setLoading(false);
  };

  const downloadPdf = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const params = new URLSearchParams();
      if (selectedState) params.set('state', selectedState);
      if (selectedIndustry) params.set('industry', selectedIndustry);
      const res = await fetch(`${API_URL}/api/intelligence/snapshot/pdf?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Market_Snapshot_${selectedState || selectedIndustry || 'Report'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
    setGenerating(false);
  };

  return (
    <IntelligenceLayout title="Snapshot Generator">
      <header className="bg-white border-b border-gray-200 px-8 py-5 shadow-sm">
        <h1 className="text-2xl font-bold text-[#0A1F44]">Market Snapshot Generator</h1>
        <p className="text-sm text-gray-500">Create a one-page market report for sales conversations</p>
      </header>

      <main className="p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="snapshot-config">
              <h3 className="text-sm font-semibold text-[#0A1F44] uppercase tracking-wider mb-4">Configure Report</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1.5">State / Region</label>
                  <select value={selectedState} onChange={e => setSelectedState(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg text-sm text-gray-700 px-3 py-2.5 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30" data-testid="state-select">
                    <option value="">All States (National)</option>
                    {(filterOptions.states || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-1.5">Industry Focus</label>
                  <select value={selectedIndustry} onChange={e => setSelectedIndustry(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-lg text-sm text-gray-700 px-3 py-2.5 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30" data-testid="industry-select">
                    <option value="">All Industries</option>
                    {(filterOptions.industries || []).map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>

                <button onClick={loadPreview} disabled={loading || (!selectedState && !selectedIndustry)}
                  className="w-full py-2.5 bg-[#0A1F44] text-white rounded-lg text-sm font-medium hover:bg-[#0A1F44]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-testid="preview-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  {loading ? 'Loading...' : 'Preview Report'}
                </button>
              </div>
            </div>

            {preview && (
              <button onClick={downloadPdf} disabled={generating}
                className="w-full py-3 bg-[#D4AF37] text-[#0A1F44] rounded-xl text-sm font-bold hover:bg-[#c9a432] disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-md" data-testid="download-pdf-btn">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {generating ? 'Generating PDF...' : 'Download PDF Report'}
              </button>
            )}
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-2">
            {preview ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-testid="snapshot-preview">
                {/* Mock PDF header */}
                <div className="bg-[#0A1F44] px-6 py-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-white">Market Snapshot: {preview.label}</h2>
                    <p className="text-xs text-gray-400">{preview.period} | Generated {new Date().toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-[#D4AF37]">OMNIA PARATUS</p>
                    <p className="text-[10px] text-[#D4AF37]">TALENT SOLUTIONS</p>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Metrics row */}
                  <div className="grid grid-cols-4 gap-4" data-testid="preview-metrics">
                    {[
                      { label: 'Total Postings', value: preview.total_postings, icon: Briefcase },
                      { label: 'Unique Roles', value: preview.top_roles?.length || 0, icon: FileText },
                      { label: 'Companies', value: preview.top_companies?.length || 0, icon: Building2 },
                      { label: 'Remote Share', value: `${preview.remote_share}%`, icon: MapPin },
                    ].map(m => (
                      <div key={m.label} className="bg-[#f0f4f8] rounded-lg p-3 text-center">
                        <m.icon className="h-4 w-4 text-[#0A1F44]/40 mx-auto mb-1" />
                        <p className="text-xl font-bold text-[#0A1F44]">{m.value}</p>
                        <p className="text-[10px] text-gray-500 uppercase">{m.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Top Roles */}
                  <div>
                    <h3 className="text-xs font-semibold text-[#0A1F44] uppercase tracking-wider mb-3">Top Roles in Demand</h3>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[10px] text-gray-500 uppercase">
                            <th className="text-left px-3 py-2">#</th>
                            <th className="text-left px-3 py-2">Job Title</th>
                            <th className="text-right px-3 py-2">Postings</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(preview.top_roles || []).slice(0, 10).map((r, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                              <td className="px-3 py-2 text-[#0A1F44] font-medium">{r.title}</td>
                              <td className="px-3 py-2 text-right text-gray-600">{r.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Companies + Industries */}
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-xs font-semibold text-[#0A1F44] uppercase tracking-wider mb-3">Top Companies Hiring</h3>
                      <div className="space-y-1.5">
                        {(preview.top_companies || []).slice(0, 8).map((c, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-gray-700"><span className="text-gray-400 mr-1">{i + 1}.</span> {c.company}</span>
                            <span className="text-gray-500">{c.count}</span>
                          </div>
                        ))}
                        {(!preview.top_companies || preview.top_companies.length === 0) && (
                          <p className="text-sm text-gray-400">No company data</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold text-[#0A1F44] uppercase tracking-wider mb-3">Top Industries</h3>
                      <div className="space-y-1.5">
                        {(preview.top_industries || []).slice(0, 8).map((ind, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span className="text-gray-700"><span className="text-gray-400 mr-1">{i + 1}.</span> {ind.industry}</span>
                            <span className="text-gray-500">{ind.count}</span>
                          </div>
                        ))}
                        {(!preview.top_industries || preview.top_industries.length === 0) && (
                          <p className="text-sm text-gray-400">No industry data</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t-2 border-[#D4AF37] pt-3 text-center">
                    <p className="text-[10px] text-gray-400">Prepared by Omnia Paratus Talent Solutions | Boston, MA | omniaparatustalent.com</p>
                    <p className="text-[10px] text-gray-400">Data sourced from multiple verified public APIs. Coverage varies by region.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-12 text-center shadow-sm">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">Generate a Market Report</h3>
                <p className="text-sm text-gray-400 max-w-md mx-auto">
                  Select a state or industry on the left and click "Preview Report" to see a client-ready market snapshot you can download as PDF.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </IntelligenceLayout>
  );
};

export default IntelligenceSnapshot;
